import { useEffect, useState, useCallback, useRef } from 'react';
import { io, Socket } from 'socket.io-client';
import type { ServiceStatus, AlertPayload, ConnectionState } from '../types';

// Use env var with fallback to origin detection for production
const MONITOR_URL =
  import.meta.env.VITE_STATUS_MONITOR_URL ||
  (typeof window !== 'undefined' && window.location.hostname !== 'localhost'
    ? window.location.origin
    : 'http://localhost:7101');

interface UseStatusMonitorOptions {
  onAlert?: (payload: AlertPayload) => void;
}

interface UseStatusMonitorReturn {
  services: ServiceStatus[];
  connection: ConnectionState;
  refresh: () => void;
  lastUpdate: string | null;
}

export function useStatusMonitor(options: UseStatusMonitorOptions = {}): UseStatusMonitorReturn {
  const { onAlert } = options;

  const [services, setServices] = useState<ServiceStatus[]>([]);
  const [lastUpdate, setLastUpdate] = useState<string | null>(null);
  const [connection, setConnection] = useState<ConnectionState>({
    isConnected: false,
    isReconnecting: false,
    lastConnected: null,
  });

  const socketRef = useRef<Socket | null>(null);
  const onAlertRef = useRef(onAlert);

  // Keep onAlert ref updated
  useEffect(() => {
    onAlertRef.current = onAlert;
  }, [onAlert]);

  // Initialize WebSocket connection
  useEffect(() => {
    const socket = io(MONITOR_URL, {
      transports: ['polling', 'websocket'], // Start with polling, then upgrade to websocket
      upgrade: true, // Allow upgrade from polling to websocket
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
    });

    socketRef.current = socket;

    // Connection events
    socket.on('connect', () => {
      console.log('[StatusMonitor] Connected to monitor server');
      setConnection({
        isConnected: true,
        isReconnecting: false,
        lastConnected: new Date().toISOString(),
      });
    });

    socket.on('disconnect', (reason) => {
      console.log('[StatusMonitor] Disconnected:', reason);
      setConnection((prev) => ({
        ...prev,
        isConnected: false,
        isReconnecting: reason !== 'io client disconnect',
      }));
    });

    socket.on('connect_error', (error) => {
      console.error('[StatusMonitor] Connection error:', error.message);
      setConnection((prev) => ({
        ...prev,
        isConnected: false,
        isReconnecting: true,
      }));
    });

    socket.io.on('reconnect_attempt', (attempt) => {
      console.log('[StatusMonitor] Reconnection attempt:', attempt);
      setConnection((prev) => ({
        ...prev,
        isReconnecting: true,
      }));
    });

    socket.io.on('reconnect', () => {
      console.log('[StatusMonitor] Reconnected');
      setConnection({
        isConnected: true,
        isReconnecting: false,
        lastConnected: new Date().toISOString(),
      });
    });

    // Status events
    socket.on('status:update', (statuses: ServiceStatus[]) => {
      console.log('[StatusMonitor] Received status update:', statuses.length, 'services');
      setServices(statuses);
      setLastUpdate(new Date().toISOString());
    });

    // Alert events
    socket.on('status:alert', (payload: AlertPayload) => {
      console.log('[StatusMonitor] Alert:', payload.type, payload.services.length, 'services');
      if (onAlertRef.current) {
        onAlertRef.current(payload);
      }
    });

    // Cleanup
    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, []);

  // Manual refresh function
  const refresh = useCallback(() => {
    if (socketRef.current?.connected) {
      console.log('[StatusMonitor] Requesting manual refresh');
      socketRef.current.emit('status:refresh');
    } else {
      console.warn('[StatusMonitor] Cannot refresh - not connected');
    }
  }, []);

  return {
    services,
    connection,
    refresh,
    lastUpdate,
  };
}
